import { useMemo } from 'react'
import { ArrowRight } from 'lucide-react'
import { useRailScroll } from '@/hooks/useRailScroll'
import RailArrow from './RailArrow'
import SchoolCard from './SchoolCard'
import { EXPLORE_ALL_PATH, PUBLIC_CONTAINER } from './publicShell'
import { cn } from '@/lib/utils'
import type { DirectorySchool } from '@/types/publicSchool'

/** Uma seção da vista lista, como o servidor a manda. */
export interface CategorySectionData {
  key: string
  label: string
  /** Etiqueta em caixa alta acima do título. Cai no `label` se a seed não rodou. */
  eyebrow: string
  headline: string
  description: string | null
  /** Quantos negócios a categoria tem no recorte atual, além dos que a seção mostra. */
  total: number
  /**
   * Referência aos cards que a página já recebeu. Ids em vez dos cards inteiros
   * porque uma página de criador multi-categoria apareceria serializada várias
   * vezes — e assim as duas vistas mostram, provadamente, os mesmos objetos.
   */
  listing_ids: string[]
}

/**
 * O recorte que a vista lista está mostrando, para o "Ver todos" levar junto.
 *
 * Sem isto o link ia só com a categoria: quem tinha filtrado Fortaleza clicava
 * em "Ver todos" e caía no Brasil inteiro, com o select de cidade limpo — o
 * filtro sumia sem ninguém ter desmarcado nada.
 */
export interface SectionFilters {
  q: string | null
  city: string | null
  service: string | null
}

interface CategorySectionsProps {
  sections: CategorySectionData[]
  /** A lista completa da página; cada seção pesca a sua por id. */
  listings: DirectorySchool[]
  filters: SectionFilters
}

/** `/explore/todos` com a categoria da seção e o recorte que já estava no ar. */
function allHref(categoryKey: string, filters: SectionFilters): string {
  const params = new URLSearchParams({ category: categoryKey })
  if (filters.q) params.set('q', filters.q)
  if (filters.city) params.set('city', filters.city)
  if (filters.service) params.set('service', filters.service)

  return `${EXPLORE_ALL_PATH}?${params}`
}

/**
 * A vista LISTA do Explore: uma seção por categoria, cada uma com etiqueta,
 * título, descrição e um carrossel horizontal dos negócios.
 *
 * É a alternativa ao mapa, não um complemento dele — quem procura "fotógrafo"
 * não quer arrastar um mapa, quer ver quem existe. As duas vistas saem da MESMA
 * lista filtrada: trocar de vista muda o desenho, nunca o resultado.
 */
export default function CategorySections({
  sections,
  listings,
  filters,
}: CategorySectionsProps) {
  // Índice por id: cada seção referencia os cards, e varrer a lista inteira por
  // seção seria O(seções × negócios) a cada render.
  const byId = useMemo(
    () => new Map(listings.map((listing) => [listing.id, listing])),
    [listings],
  )

  return (
    // `pt-4` e não `py-10`: acima já vem o card de busca com o próprio respiro,
    // e somando os dois o conteúdo de verdade nascia abaixo da dobra por causa
    // de ar vazio. Entre as seções o espaço continua largo — ali ele separa
    // assuntos diferentes.
    <div className="space-y-14 pb-10 pt-4">
      {sections.map((section) => (
        <CategorySection
          key={section.key}
          section={section}
          filters={filters}
          listings={section.listing_ids
            .map((id) => byId.get(id))
            .filter((item): item is DirectorySchool => Boolean(item))}
        />
      ))}
    </div>
  )
}

function CategorySection({
  section,
  listings,
  filters,
}: {
  section: CategorySectionData
  listings: DirectorySchool[]
  filters: SectionFilters
}) {
  // Um id por seção: a página monta vários trilhos, e o `aria-controls` das
  // setas precisa apontar para o certo.
  const railId = `secao-${section.key}`
  const { ref, dragProps, canScrollPrev, canScrollNext, scrollByPage } =
    useRailScroll<HTMLUListElement>({
      id: railId,
      // Trocar de filtro troca os negócios da seção sem remontar o componente
      // (Inertia com preserveState) — sem isto o trilho continuaria no scroll da
      // lista anterior.
      resetKey: section.listing_ids.join(','),
    })

  const hasControls = canScrollPrev || canScrollNext

  return (
    <section aria-labelledby={`${railId}-titulo`}>
      {/* O TEXTO respeita a faixa; o TRILHO não. É essa divisão que dá o efeito
          de "tem mais coisa ali fora" em tela larga: com os dois dentro do
          container, acima de 1760px o trilho parava no meio da tela e sobrava
          uma faixa vazia à direita. */}
      <div
        className={cn(
          PUBLIC_CONTAINER,
          'flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between',
        )}
      >
        <div className="max-w-3xl">
          {/* Caixa alta pelo CSS, não pelo dado: o texto no banco continua
              legível para quem for editá-lo na seed. */}
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">
            {section.eyebrow}
          </p>
          <h2
            id={`${railId}-titulo`}
            className="mt-3 text-2xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-3xl"
          >
            {section.headline}
          </h2>
          {section.description && (
            <p className="mt-3 text-base leading-relaxed text-gray-600">
              {section.description}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* As setas ficam ao lado do "Ver todos", e não flutuando sobre os
              cards: sobre a foto elas cobrem justamente o que a pessoa veio ver.
              Só somem quando o trilho inteiro cabe na tela — aí não há o que
              rolar e elas seriam dois botões mortos. */}
          {hasControls && (
            <>
              <RailArrow
                direction="prev"
                disabled={!canScrollPrev}
                controls={railId}
                noun={section.label.toLowerCase()}
                onClick={() => scrollByPage(-1)}
              />
              <RailArrow
                direction="next"
                disabled={!canScrollNext}
                controls={railId}
                noun={section.label.toLowerCase()}
                onClick={() => scrollByPage(1)}
              />
            </>
          )}

          {/* Leva à listagem completa da categoria (/explore/todos), com
              busca e filtro, carregando o recorte que já estava no ar. A seção
              mostra no máximo os primeiros; este é o caminho para o resto.

              Sem contagem no rótulo: o número ao lado de "Ver todos" lia como
              parte do botão e competia com o título da seção. Quantos há é o
              que a própria listagem diz ao abrir. */}
          <a
            href={allHref(section.key, filters)}
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:border-primary hover:text-primary"
          >
            Ver todos
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* `public-rail` (application.css) põe o gutter dos dois lados a partir da
          MESMA faixa de 1760px do container, então o primeiro card nasce alinhado
          com o título acima e o resto sangra até a borda da tela. Ele já traz o
          `scroll-padding` junto — sem isso o snap encostaria o card na borda e
          comeria justamente o gutter. */}
      <ul
        ref={ref}
        {...dragProps}
        className="public-rail mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {listings.map((listing) => (
          <li
            key={listing.id}
            className="w-[78%] shrink-0 snap-start sm:w-[46%] lg:w-[31%] xl:w-[23%]"
          >
            <SchoolCard school={listing} showServiceTag />
          </li>
        ))}
      </ul>
    </section>
  )
}
